/**
 * fal.ai Service
 * Handles image generation via fal.ai API
 * Supports: Z-Image Turbo, Qwen Image, Qwen Image Edit, Seedream 4.5, Recraft V3
 * 
 * Uses async queue pattern to avoid 524 Cloudflare timeouts:
 * 1. Submit job to /api/falai/generate/async
 * 2. Poll /api/falai/image/job/:jobId for completion
 */

import logger from '../utils/logger';
import { supabase } from '../lib/supabase';

// Polling configuration (matches video service)
const POLL_INTERVAL_MS = 3000; // Poll every 3 seconds
const MAX_POLL_TIME_MS = 30 * 60 * 1000; // 30 minute max

export interface FalAIImageRequest {
  prompt: string;
  model: string;
  aspectRatio?: string;
  quality?: string;
  negativePrompt?: string;
  style?: string; // For Recraft V3
  enablePromptExpansion?: boolean; // For Z-Image Turbo
  imageUrl?: string; // Single image URL (backward compat)
  imageUrls?: string[]; // Array of image URLs for multi-image support
  strength?: number; // For Z-Image image-to-image (0-1, default 0.6)
  flowId?: string; // Flow ID for scoped storage
  resolution?: string; // Kling Image: '1K' | '2K' | '4K'. Also drives server-side credit pricing.
  outputFormat?: string; // Kling Image: 'png' | 'jpeg' | 'webp'
}

export interface FalAIImageResponse {
  imageUrl: string;
  model: string;
  processingTime?: number;
  width?: number;
  height?: number;
}

export interface FalAIError {
  message: string;
  code?: string;
  details?: any;
  suggestions?: string[];
}

class FalAIService {
  // Use relative URL in production, localhost for development
  private baseUrl: string = process.env.REACT_APP_API_URL || 
    (typeof window !== 'undefined' && window.location.hostname === 'localhost' 
      ? 'http://localhost:3001' 
      : '');

  /**
   * Generate an image using fal.ai models
   * Uses async queue pattern to avoid 524 Cloudflare timeouts
   */
  async generateImage(request: FalAIImageRequest): Promise<FalAIImageResponse> {
    const { 
      prompt, 
      model, 
      aspectRatio = '1:1', 
      quality = 'balanced',
      negativePrompt,
      style,
      enablePromptExpansion,
      imageUrl,
      imageUrls,
      strength,
      flowId,
      resolution,
      outputFormat,
    } = request;

    logger.debug('[FalAI] Sending async image generation request:', {
      prompt: prompt.substring(0, 100),
      model,
      aspectRatio,
      quality,
      hasImageUrl: !!imageUrl,
      imageUrlsCount: imageUrls?.length || 0,
      strength,
      flowId,
    });

    try {
      // Get current user for credit tracking and R2 upload
      const { data: { user } } = await supabase.auth.getUser();
      
      // Build headers with user ID for credit tracking and R2 upload
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user?.id) {
        headers['x-user-id'] = user.id;
      }

      // Submit job to async endpoint (returns immediately with jobId)
      const response = await fetch(`${this.baseUrl}/api/falai/generate/async`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt,
          model,
          aspectRatio,
          quality,
          negativePrompt,
          style,
          enablePromptExpansion,
          imageUrl,
          imageUrls,
          strength,
          flowId,
          resolution,
          outputFormat,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));

        logger.error('[FalAI] Async job submission failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
        });

        // Create enhanced error with suggestions from backend
        const error = {
          message: errorData.error || `API request failed with status ${response.status}`,
          code: errorData.errorCode || response.status.toString(),
          details: errorData.details,
          suggestions: errorData.suggestions,
        } as FalAIError;
        throw error;
      }

      const submitResult = await response.json();
      
      // Poll for completion using the jobId
      if (submitResult.jobId) {
        logger.debug('[FalAI] Job submitted, polling for completion:', submitResult.jobId);
        return await this.pollForCompletion(submitResult.jobId, headers);
      }
      
      // Legacy sync response (fallback for backward compatibility)
      logger.debug('[FalAI] Image generated successfully (sync):', {
        model: submitResult.model,
        processingTime: submitResult.processingTime,
      });

      return {
        imageUrl: submitResult.imageUrl,
        model: submitResult.model,
        processingTime: submitResult.processingTime,
        width: submitResult.width,
        height: submitResult.height,
      };
    } catch (error: any) {
      // Handle network errors
      if (error.message?.includes('fetch') || error.message?.includes('network')) {
        logger.error('[FalAI] Network error:', error);
        throw {
          message: 'Unable to connect to backend server. Please ensure the server is running.',
          code: 'NETWORK_ERROR',
        } as FalAIError;
      }

      // Re-throw if already formatted
      if (error.code) {
        throw error;
      }

      // Generic error
      logger.error('[FalAI] Unexpected error:', error);
      throw {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
      } as FalAIError;
    }
  }

  /**
   * Poll for job completion
   * Returns the image data when the job is complete
   */
  private async pollForCompletion(jobId: string, headers: Record<string, string>): Promise<FalAIImageResponse> {
    const startTime = Date.now();
    
    while (Date.now() - startTime < MAX_POLL_TIME_MS) {
      try {
        const response = await fetch(`${this.baseUrl}/api/falai/image/job/${jobId}`, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw {
            message: errorData.error || `Failed to check job status: ${response.status}`,
            code: errorData.errorCode || response.status.toString(),
          } as FalAIError;
        }

        const jobStatus = await response.json();
        
        logger.debug(`[FalAI] Job ${jobId} status: ${jobStatus.status}, progress: ${jobStatus.progress}%`);

        if (jobStatus.status === 'completed' && jobStatus.imageUrl) {
          logger.debug(`[FalAI] Job ${jobId} completed!`);
          return {
            imageUrl: jobStatus.imageUrl,
            model: jobStatus.model,
            processingTime: jobStatus.processingTime,
            width: jobStatus.width,
            height: jobStatus.height,
          };
        }

        if (jobStatus.status === 'failed') {
          throw {
            message: jobStatus.error || 'Image generation failed',
            code: jobStatus.errorCode || 'GENERATION_FAILED',
          } as FalAIError;
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      } catch (error: any) {
        // Re-throw job failures, but log and continue for network errors
        if (error.code && error.code !== 'NETWORK_ERROR') {
          throw error;
        }
        logger.warn('[FalAI] Poll error (retrying):', error.message);
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    }

    throw {
      message: 'Image generation timed out after 30 minutes',
      code: 'TIMEOUT',
    } as FalAIError;
  }
}

// Export singleton instance
export const falAIService = new FalAIService();
export default falAIService;
